// /parent/medications — manage medication consents per child + see
// the read-only administration log written by teachers.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Pill,
  Plus,
  Pencil,
  ProhibitInset,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listChildrenForParent } from "@/lib/dailyReports";
import {
  formatAdministeredAt,
  isAuthorizationActiveToday,
  listAdministrationsForChild,
  listAuthorizationsForChild,
  routeLabel,
  withdrawAuthorization,
} from "@/lib/medications";
import ChildAvatar from "@/components/children/ChildAvatar";
import MedicationConsentSheet from "@/components/medications/MedicationConsentSheet";

export default function ParentMedications() {
  const { profile } = useAuth();
  const [children, setChildren] = useState([]);
  const [auths, setAuths] = useState({}); // childId -> list
  const [logs, setLogs] = useState({});   // childId -> list
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState({ open: false, child: null, existing: null });

  const refresh = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const kids = await listChildrenForParent(profile.id);
      setChildren(kids || []);
      const byA = {};
      const byL = {};
      await Promise.all(
        (kids || []).map(async (c) => {
          byA[c.id] = await listAuthorizationsForChild(c.id, { activeOnly: false });
          byL[c.id] = await listAdministrationsForChild(c.id, { limit: 5 });
        }),
      );
      setAuths(byA);
      setLogs(byL);
    } catch (err) {
      toast.error(err.message || "Could not load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const onAdd = (child) => setSheet({ open: true, child, existing: null });
  const onEdit = (child, row) => setSheet({ open: true, child, existing: row });

  const onWithdraw = async (row) => {
    if (
      !window.confirm(
        `Withdraw consent for ${row.medication_name}? Teachers will no longer be able to give this medication.`,
      )
    )
      return;
    try {
      await withdrawAuthorization({ id: row.id, profileId: profile.id });
      toast.success(`Withdrew consent for ${row.medication_name}`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not withdraw");
    }
  };

  return (
    <div data-testid="parent-medications" className="space-y-5">
      <div>
        <Link
          to="/parent"
          data-testid="parent-medications-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" />
          Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Medications
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Authorise medications and review every dose teachers
          administer to your child. Consent and the administration log
          are kept separate so you can trust each.
        </p>
      </div>

      {loading && (
        <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          Loading…
        </p>
      )}

      {!loading && children.length === 0 && (
        <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          No children linked to your family yet.
        </p>
      )}

      <ul className="space-y-5" data-testid="parent-medications-children">
        {children.map((child) => {
          const list = auths[child.id] || [];
          const log = logs[child.id] || [];
          const active = list.filter(isAuthorizationActiveToday);
          const inactive = list.filter((a) => !isAuthorizationActiveToday(a));
          return (
            <li
              key={child.id}
              data-testid={`parent-medications-child-${child.id}`}
              className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_2px_12px_rgba(20,22,26,0.04)] sm:p-5"
            >
              <header className="flex items-center gap-3">
                <ChildAvatar
                  path={child.profile_photo_path}
                  name={`${child.first_name || ""} ${child.last_name || ""}`}
                  size={40}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                    {child.first_name} {child.last_name || ""}
                  </p>
                  <p className="text-[11px] text-neutral-500">
                    {active.length} active · {inactive.length} archived
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`parent-medications-add-${child.id}`}
                  onClick={() => onAdd(child)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover"
                >
                  <Plus size={12} weight="bold" />
                  Authorise
                </button>
              </header>

              {list.length === 0 ? (
                <p className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-3 text-center text-[12px] text-neutral-500">
                  No medications authorised.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {list.map((row) => (
                    <ConsentRow
                      key={row.id}
                      row={row}
                      onEdit={() => onEdit(child, row)}
                      onWithdraw={() => onWithdraw(row)}
                    />
                  ))}
                </ul>
              )}

              {/* Administration log preview */}
              {log.length > 0 && (
                <section className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                    Recent doses given
                  </p>
                  <ul className="mt-1 space-y-1">
                    {log.map((r) => (
                      <li
                        key={r.id}
                        data-testid={`parent-medications-log-${r.id}`}
                        className="rounded-xl border border-neutral-200 bg-white px-2 py-1 text-[12px] text-neutral-700"
                      >
                        <strong>{r.medication_name}</strong> · {r.dose_given} ·{" "}
                        {formatAdministeredAt(r.administered_at)}
                        {r.administrator?.full_name
                          ? ` · ${r.administrator.full_name}`
                          : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </li>
          );
        })}
      </ul>

      <MedicationConsentSheet
        open={sheet.open}
        onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))}
        child={sheet.child}
        existing={sheet.existing}
        authorizedBy={profile?.id}
        onSaved={() => refresh()}
      />
    </div>
  );
}

function ConsentRow({ row, onEdit, onWithdraw }) {
  const activeToday = isAuthorizationActiveToday(row);
  return (
    <li
      data-testid={`parent-medications-row-${row.id}`}
      data-active={activeToday ? "true" : "false"}
      className={`flex items-start gap-3 rounded-2xl border p-3 ${
        activeToday
          ? "border-neutral-200 bg-white"
          : "border-neutral-200 bg-neutral-50/40"
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          activeToday ? "bg-primary/10 text-primary-hover" : "bg-neutral-200 text-neutral-500"
        }`}
        aria-hidden
      >
        <Pill size={16} weight="duotone" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-neutral-900">
          {row.medication_name}
          {!activeToday && (
            <span className="ml-2 rounded-full bg-neutral-200 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-neutral-600">
              Inactive
            </span>
          )}
        </p>
        <p className="mt-0.5 text-[12px] text-neutral-600">
          {row.dosage} · {routeLabel(row.route)}
          {row.schedule ? ` · ${row.schedule}` : ""}
        </p>
        <p className="mt-0.5 text-[11px] text-neutral-500">
          {row.starts_on}
          {row.ends_on ? ` → ${row.ends_on}` : " · ongoing"}
        </p>
        {row.warnings && (
          <p className="mt-1 inline-flex items-start gap-1 rounded-xl border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
            <Warning size={11} weight="duotone" className="mt-0.5" aria-hidden />
            {row.warnings}
          </p>
        )}
      </div>
      {activeToday && (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            data-testid={`parent-medications-edit-${row.id}`}
            onClick={onEdit}
            className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <Pencil size={10} weight="bold" className="inline" /> Edit
          </button>
          <button
            type="button"
            data-testid={`parent-medications-withdraw-${row.id}`}
            onClick={onWithdraw}
            className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
          >
            <ProhibitInset size={10} weight="bold" className="inline" /> Withdraw
          </button>
        </div>
      )}
    </li>
  );
}
